import { getMaxTravelDate } from '@massilia/contracts'
import {
  applyIAmTravelerFill,
  ESTIMATE_FORM_DEFAULT_VALUES,
  EstimateFormGroup,
  type EstimateFormType,
  EstimateTypeSelector,
  makeEstimateFormSchema,
  routeSavedPassengers,
  useEstimateForm,
} from '@massilia/features'
import {
  type CreateEstimateRequest,
  type Passenger,
  toError,
  useCreateEstimateMutation,
  useGetMyPassengersQuery,
  useGetMyProfileQuery,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { useAuthStore } from '@/stores/auth.store'

interface EstimateFormProps {
  /** Bouton « Retour ». Par défaut : retour à la recherche. */
  onCancel?: () => void
  /** Après succès. Par défaut : redirection vers la page « devis envoyé ». */
  onSuccess?: () => void
  /**
   * Envoi de la demande. Par défaut l'endpoint public (upsert du client par
   * email) ; l'espace client passe le sien, qui impose le client du compte.
   *
   * Le formulaire ne lit pas la réponse — il redirige — d'où le `void` : l'appelant
   * n'a pas à s'engager sur la forme de ce qu'il renvoie.
   */
  submit?: (request: CreateEstimateRequest) => Promise<void>
}

export function EstimateForm({ onCancel, onSuccess, submit }: EstimateFormProps = {}) {
  const navigate = useNavigate()
  const createMutation = useCreateEstimateMutation()
  const { isAuthenticated, user } = useAuthStore()
  const submitEstimate = submit ?? createMutation.mutateAsync

  const { data: profile } = useGetMyProfileQuery(isAuthenticated)
  const { data: myPassengers } = useGetMyPassengersQuery(isAuthenticated)

  const phoneNumber =
    profile?.client?.phoneNumbers?.find((p) => p.type === 'PRINCIPAL')?.number ??
    profile?.client?.phoneNumbers?.[0]?.number ??
    ''

  const form = useEstimateForm({
    defaultValues: {
      ...ESTIMATE_FORM_DEFAULT_VALUES,
      clientFirstName:
        isAuthenticated && user ? user.firstName : ESTIMATE_FORM_DEFAULT_VALUES.clientFirstName,
      clientLastName:
        isAuthenticated && user ? user.lastName : ESTIMATE_FORM_DEFAULT_VALUES.clientLastName,
      contactEmail:
        isAuthenticated && user ? user.email : ESTIMATE_FORM_DEFAULT_VALUES.contactEmail,
    } satisfies EstimateFormType,
    enforceTravelHorizon: true,
    onSubmit: async (value) => {
      // Les lignes préremplies depuis un passager enregistré sont routées vers
      // `existingPassengerIds` (lien, pas de doublon) avant validation/envoi.
      const routed = routeSavedPassengers(value)
      const parsed = makeEstimateFormSchema({ maxTravelDate: getMaxTravelDate() }).safeParse(routed)

      if (!parsed.success) {
        toast.error('Formulaire invalide - Veuillez vérifier les champs')
        return
      }

      try {
        await submitEstimate(parsed.data)
      } catch (error) {
        // Le `catch {}` nu jetait le message de l'API : un refus explicite du serveur
        // (profil incomplet, voyageur non rattaché…) s'affichait comme « Impossible de
        // créer le devis », sans rien pour comprendre ni côté client ni côté support.
        toast.error('❌ Une erreur est survenue', {
          description: resolveAuthErrorMessage(toError(error), {
            fallbackMessage: 'Impossible de créer le devis',
          }),
        })
        return
      }

      if (onSuccess) {
        onSuccess()
        return
      }

      void navigate({ to: '/devis-envoye' })
    },
  })

  // Sync phone number once profile loads (async)
  const phoneNumberSynced = useRef(false)
  useEffect(() => {
    if (phoneNumber && isAuthenticated && !phoneNumberSynced.current) {
      form.setFieldValue('contactPhoneNumber', phoneNumber)
      phoneNumberSynced.current = true
    }
  }, [phoneNumber, isAuthenticated, form])

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <EstimateTypeSelector
        fields={{
          estimateType: 'estimateType',
          type: 'type',
        }}
        form={form}
      />

      <EstimateFormGroup
        enforceTravelHorizon
        readOnlyClient={isAuthenticated}
        clientPassengers={myPassengers as (Passenger & { id: string })[] | undefined}
        editablePhone={isAuthenticated && !!profile && !phoneNumber}
        onIAmTraveler={() => applyIAmTravelerFill(form)}
        fields={{
          clientFirstName: 'clientFirstName',
          clientLastName: 'clientLastName',
          contactEmail: 'contactEmail',
          approximativeBudget: 'approximativeBudget',
          carLoan: 'carLoan',
          companyPreference: 'companyPreference',
          complementaryInformation: 'complementaryInformation',
          contactPhoneNumber: 'contactPhoneNumber',
          departure: 'departure',
          departureDate: 'departureDate',
          departureFlexibilityAfter: 'departureFlexibilityAfter',
          departureFlexibilityBefore: 'departureFlexibilityBefore',
          destination: 'destination',
          estimateType: 'estimateType',
          existingPassengerIds: 'existingPassengerIds',
          flights: 'flights',
          hotelMinimumNumberOfStars: 'hotelMinimumNumberOfStars',
          maxStayDuration: 'maxStayDuration',
          newPassengers: 'newPassengers',
          onSiteActivities: 'onSiteActivities',
          particularities: 'particularities',
          pension: 'pension',
          returnDate: 'returnDate',
          returnFlexibilityAfter: 'returnFlexibilityAfter',
          returnFlexibilityBefore: 'returnFlexibilityBefore',
          transfertFromAirport: 'transfertFromAirport',
          travelClass: 'travelClass',
          type: 'type',
        }}
        form={form}
      />

      <div className="flex justify-between gap-4 pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            if (onCancel) {
              onCancel()
              return
            }
            void navigate({ to: '/recherche' })
          }}
        >
          {onCancel ? 'Annuler' : 'Retour'}
        </Button>
        <Button type="submit">Envoyer la demande</Button>
      </div>
    </form>
  )
}
